/**
 * Locale facts derived from the runtime, shared by every client.
 *
 * The rule this file exists to enforce: nothing about the user's language,
 * script direction, number format or phone conventions may be a constant in the
 * source. A single launch market is a go-to-market decision and it changes; a
 * hardcoded locale is a rewrite.
 */

/** BCP-47 tags the runtime says the user prefers, best first. */
export function preferredLocales(): string[] {
  const nav = globalThis.navigator as { languages?: readonly string[]; language?: string } | undefined;
  const fromNavigator = nav?.languages?.length ? [...nav.languages] : nav?.language ? [nav.language] : [];
  if (fromNavigator.length > 0) return fromNavigator;

  // Node, React Native without a navigator shim, and workers all land here.
  // Intl always resolves to something, even if only the system default.
  try {
    return [new Intl.DateTimeFormat().resolvedOptions().locale];
  } catch {
    return [];
  }
}

/**
 * Pick the best available locale from a catalogue.
 *
 * Falls back along the tag: `pt-BR` will accept `pt` before giving up. Returns
 * `fallback` only when nothing matches, so a user whose language we do not
 * carry gets a usable app rather than an empty one.
 */
export function negotiateLocale(available: readonly string[], fallback: string): string {
  const supported = available.map((tag) => tag.toLowerCase());
  for (const wanted of preferredLocales()) {
    const tag = wanted.toLowerCase();
    const exact = supported.indexOf(tag);
    if (exact !== -1) return available[exact];

    const base = tag.split('-')[0];
    const partial = supported.findIndex((s) => s === base || s.startsWith(`${base}-`));
    if (partial !== -1) return available[partial];
  }
  return fallback;
}

/**
 * Writing direction for a locale.
 *
 * Asked of `Intl.Locale` where the runtime implements `getTextInfo`, because a
 * hand-maintained list of RTL languages is a list that will be missing one.
 * The fallback list is only for older runtimes, and covers the RTL scripts in
 * actual use: Arabic, Hebrew, Persian, Urdu, Pashto, Sindhi, Divehi, Yiddish,
 * Kurdish (Sorani), Uyghur, Syriac, N'Ko, Samaritan, Mandaic, Thaana.
 */
const RTL_LANGUAGES = new Set([
  'ar', 'he', 'iw', 'fa', 'ur', 'ps', 'sd', 'dv', 'yi', 'ckb', 'ug', 'syr', 'nqo', 'smp', 'mid', 'th-thaa',
]);

export function directionOf(locale: string): 'ltr' | 'rtl' {
  try {
    const intlLocale = new Intl.Locale(locale) as Intl.Locale & {
      getTextInfo?: () => { direction: string };
      textInfo?: { direction: string };
    };
    const info = intlLocale.getTextInfo?.() ?? intlLocale.textInfo;
    if (info?.direction === 'rtl' || info?.direction === 'ltr') return info.direction;

    // Script subtag is decisive when present: `az-Arab` is RTL, `az-Latn` is not.
    const script = intlLocale.script;
    if (script) {
      return ['Arab', 'Hebr', 'Thaa', 'Syrc', 'Nkoo', 'Samr', 'Mand', 'Adlm'].includes(script)
        ? 'rtl'
        : 'ltr';
    }
    return RTL_LANGUAGES.has(intlLocale.language ?? '') ? 'rtl' : 'ltr';
  } catch {
    return RTL_LANGUAGES.has(locale.toLowerCase().split('-')[0]) ? 'rtl' : 'ltr';
  }
}

/**
 * The user's region as ISO 3166-1 alpha-2, for parsing phone numbers written in
 * national format.
 *
 * This belongs to the device, not to the server. A server hosted in one country
 * has no business deciding how a visitor from another country writes their own
 * phone number -- that assumption is exactly what makes a product feel local to
 * one market and broken everywhere else.
 */
let regionOverride: string | undefined;

/**
 * Tell the protocol what region the device is set to.
 *
 * Language and region are *different settings*, and inferring one from the
 * other is wrong on the platforms that matter. A phone set to Hebrew reports
 * the tag `he`, with no region in it at all — so a user in Israel typing
 * `052-…` had their own number rejected as unparseable. Only the OS knows the
 * region; on React Native `expo-localization` reads it and the client passes it
 * here at startup.
 */
export function setDeviceRegion(region: string | undefined): void {
  regionOverride = region?.trim() || undefined;
}

export function deviceRegion(): string | undefined {
  if (regionOverride) return regionOverride;

  // Browsers usually supply a full tag like `en-US`, so inference still works
  // there and this stays a fallback rather than the only path.
  for (const tag of preferredLocales()) {
    try {
      const region = new Intl.Locale(tag).region;
      if (region) return region;
    } catch {
      const match = /^[A-Za-z]{2,3}[-_]([A-Za-z]{2})\b/.exec(tag);
      if (match) return match[1].toUpperCase();
    }
  }
  return undefined;
}

/**
 * Wrap user-supplied text in Unicode directional isolates.
 *
 * Without this, a message, name or filename whose direction differs from the
 * surrounding UI reorders the text around it: a Hebrew display name in an
 * English sentence drags trailing punctuation to the wrong end, and a phone
 * number inside an RTL line renders its digits in the wrong place. It is the
 * most common bidi bug there is, and it is invisible to anyone testing in one
 * language.
 *
 * FSI picks the direction from the content's first strong character, so this
 * works without knowing what language the text is in.
 */
export function isolate(text: string): string {
  return `⁨${text}⁩`;
}
